import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLocationData } from "../../../data/use-location-data";
import { useSignupForm } from "./use-signup-form";
import { useSignupSubmit } from "./use-signup-submit";
import { useSocialSignup } from "./use-social-signup";

export { useSocialSignup };

export function useFormState(initialValue) {
  const [state, setState] = useState(initialValue);
  return [state, setState];
}

export function useSignupState() {
  const location = useLocation();

  /* UI-only state (search/filter dropdowns, modal, password visibility) */
  const [ui, setUi] = useState({
    showPassword: "password",
    showModal: false,
    searchCountry: "",
    searchState: "",
    searchCity: "",
    selectedCountry: "",
    selectedState: "",
    selectedCity: "",
    hidefield: 1,
    hidestate: 1,
    hidecity: 1,
    changeicon: 1,
  });

  const setUiField = (key, value) =>
    setUi((prev) => ({ ...prev, [key]: value }));

  const [gpsCoords, setGpsCoords] = useState({ latitude: null, longitude: null });
  const [clientIp, setClientIp] = useState("");

  useEffect(() => {
    // 1. Fetch public IP client-side to bypass private IP/WiFi ranges
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.ip) {
          setClientIp(data.ip);
        }
      })
      .catch((err) => console.warn("Failed to fetch public IP client-side:", err.message));

    // 2. Fetch browser GPS coordinates
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Geolocation permission denied or failed:", error.message);
        }
      );
    }
  }, []);

  const locationData = useLocationData();
  const form = useSignupForm();
  const submit = useSignupSubmit(form.userData, { ...gpsCoords, clientIp });
  const social = useSocialSignup();

  // Convenience aliases and setters expected by legacy UI
  const setShowPassword = (val) => setUiField("showPassword", val);
  const setChangeicon = (val) => setUiField("changeicon", val);
  const setHidefield = (val) => setUiField("hidefield", val);
  const setHidestate = (val) => setUiField("hidestate", val);
  const setHidecity = (val) => setUiField("hidecity", val);
  const handleShow = () => setUiField("showModal", true);
  const handleClose = () => setUiField("showModal", false);

  const handleCountry = (e) => {
    const val = e?.target?.value || "";
    setUi((prev) => ({
      ...prev,
      selectedCountry: val,
      selectedState: "",
      selectedCity: "",
    }));
    form.setUserData((prev) => ({
      ...prev,
      country: val,
      state: "",
      city: "",
    }));
  };

  const handleState = (e) => {
    const val = e?.target?.value || "";
    setUi((prev) => ({
      ...prev,
      selectedState: val,
      selectedCity: "",
    }));
    form.setUserData((prev) => ({
      ...prev,
      state: val,
      city: "",
    }));
  };

  const handleCity = (e) => {
    const val = e?.target?.value || "";
    setUi((prev) => ({
      ...prev,
      selectedCity: val,
    }));
    form.setUserData((prev) => ({
      ...prev,
      city: val,
    }));
  };

  const userDatahandler = submit.handleSubmit;
  const isRegisterLoading = submit.isLoading || submit.isRedirecting;

  const fullnameRef = form.fieldRefs?.fullname;
  const emailRef = form.fieldRefs?.email;
  const passwordRef = form.fieldRefs?.password;
  const phonenumberRef = form.fieldRefs?.phonenumber;
  const dobRef = form.fieldRefs?.dob;

  /* Cleanup navigation timer on unmount */
  useEffect(() => {
    return () => submit.cancelNavigation();
  }, []);

  return {
    ...ui,
    ...social,
    setUiField,
    location,
    ...locationData,
    ...form,
    ...submit,
    // new convenience aliases for legacy UI
    setShowPassword,
    setChangeicon,
    setHidefield,
    setHidestate,
    setHidecity,
    handleShow,
    handleClose,
    handleCountry,
    handleState,
    handleCity,
    userDatahandler,
    isRegisterLoading,
    fullnameRef,
    emailRef,
    passwordRef,
    phonenumberRef,
    dobRef,
    // legacy name compatibility
    searchTerm: ui.searchCountry,
    searchTermone: ui.searchState,
    searchTermtwo: ui.searchCity,
    selectedstateone: ui.selectedState,
    selectedcity: ui.selectedCity,
    show: ui.showModal,
    country: locationData.country ?? locationData.countryList,
    getState: locationData.getState ?? locationData.stateList,
    getcity: locationData.getcity ?? locationData.cityList,
  };
}
